import type {
  LookalikePersona,
  LookalikeSchema,
  WeightedValue,
} from '@repo/contracts';

import type { LookalikeAxisField } from '../store/persona-lookalikes-store';
import {
  CATEGORICAL_FIELDS,
  categoricalValueOf,
  schemaFieldFor,
} from './lookalike-fields';

export const CHART_WIDTH = 720;
export const CHART_HEIGHT = 400;
export const CHART_PADDING = { bottom: 30, left: 42, right: 16, top: 14 };

/** Deterministic, cheap pseudo-jitter keyed off a lookalike's own index —
 *  not `Math.random()`: positions must stay put across re-renders (a weight
 *  change re-samples the *population*, but a point that's still there
 *  shouldn't visibly hop for no reason), and this needs no state to do it. */
function jitter(index: number, salt: number): number {
  const x = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x) - 0.5;
}

function continuousPosition(
  value: number,
  range: { min: number; max: number },
  pixelStart: number,
  pixelEnd: number,
): number {
  const span = range.max - range.min;
  const fraction = span > 0 ? (value - range.min) / span : 0.5;
  return (
    pixelStart + Math.min(1, Math.max(0, fraction)) * (pixelEnd - pixelStart)
  );
}

function categoricalPosition(
  value: string,
  pool: readonly WeightedValue[],
  index: number,
  jitterSalt: number,
  pixelStart: number,
  pixelEnd: number,
): number {
  const slot = Math.max(
    0,
    pool.findIndex((entry) => entry.value === value),
  );
  const slotWidth = (pixelEnd - pixelStart) / pool.length;
  const center = pixelStart + (slot + 0.5) * slotWidth;
  return center + jitter(index, jitterSalt) * slotWidth * 0.7;
}

function valueOf(
  lookalike: LookalikePersona,
  field: LookalikeAxisField,
): number | string {
  if (field === 'age') return lookalike.age;
  if (field === 'experienceYears') return lookalike.experienceYears;
  return categoricalValueOf(lookalike, field);
}

/** Field-view axis labels — what a dropdown (and the chart's own axis
 *  labels) show for each option. The two continuous fields are named here;
 *  every categorical one is derived from `CATEGORICAL_FIELDS`, the same
 *  source the filter dropdown and weight sliders use. */
export const AXIS_LABELS: Record<LookalikeAxisField, string> =
  Object.fromEntries([
    ['age', 'Age'],
    ['experienceYears', 'Years of experience'],
    ...CATEGORICAL_FIELDS.map((f) => [f.personaField, f.label]),
  ]) as Record<LookalikeAxisField, string>;

/**
 * Positions a lookalike on one axis for the field view: continuous fields map
 * straight onto their schema range, categorical fields get one slot per pool
 * value with a little jitter so a slot doesn't read as a single vertical
 * line. No projection, no model, no embedding — see `LookalikeSchema`'s own
 * doc on why this view needs none of that.
 */
export function fieldAxisPosition(
  lookalike: LookalikePersona,
  field: LookalikeAxisField,
  schema: LookalikeSchema,
  pixelStart: number,
  pixelEnd: number,
  jitterSalt: number,
): number {
  const value = valueOf(lookalike, field);
  if (field === 'age')
    return continuousPosition(
      value as number,
      schema.ageRange,
      pixelStart,
      pixelEnd,
    );
  if (field === 'experienceYears') {
    return continuousPosition(
      value as number,
      schema.experienceYearsRange,
      pixelStart,
      pixelEnd,
    );
  }
  const pool = schema[schemaFieldFor(field)];
  return categoricalPosition(
    value as string,
    pool,
    lookalike.index,
    jitterSalt,
    pixelStart,
    pixelEnd,
  );
}
